import { Component, inject, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Signal } from '../types';
import { ColoredChipListComponent } from '../../../shared/components/colored-chip-list/colored-chip-list.component';
import { ColoredChip } from '../../../shared/types/common';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { DashboardService } from '../../../services/dashboard.service';
import { PosthogService } from '../../../services/posthog.service';
import { PosthogEventName, PosthogEventCode } from '../../../services/types';
import { AuthorizationService } from '../../../services/authorization.service';
import { PolarisRuntimeFacadeService } from '../../../services/polaris/polaris-runtime-facade.service';
import { PolarisStartChatOptionsVm } from '../../../services/polaris/model';
import { PolarisRoutePaths, RoutePaths } from '../../../shared/types/common';

const SIGNAL_ANALYSIS_PROMPT =
  'Please elaborate on this signal. Explain what data supports it and what should I do to reach the expected impact.';

@Component({
  selector: 'app-signal-card',
  imports: [
    ColoredChipListComponent,
    AssetVersionPipe,
  ],
  templateUrl: './signal-card.component.html',
  styleUrl: './signal-card.component.scss'
})
export class SignalCardComponent {

  @Input() signalData!: Signal;
  @Input() extraText = '';

  private router = inject(Router);
  private dashboardService = inject(DashboardService);
  private posthogService = inject(PosthogService);
  private polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);

  get coloredChipList(): ColoredChip[]  {
    return this.signalData?.tags.map(item => ({
      text: item,
      color: "#4E5258",
      borderColor: "#CDD1D7",
      backgroundColor: "#FFFFFF"
    })) || [];
  }

  protected onSendMessage(): void {
    const competitorKeys =
      this.dashboardService.brandsOptions().brandCompetitorsResponse?.[this.signalData.brandKey]?.competitors?.map(
        c => c.key
      ) ?? [];
    const startChatOptions: PolarisStartChatOptionsVm = {
      signal: this.signalData,
      origin: 'signals_screen'
    };

    this.polarisRuntimeFacadeService.setUserBrandContext(this.signalData.brandKey, competitorKeys);
    this.posthogService.capture(PosthogEventName.POLARIS_PROMPT_SENT, {
      [PosthogEventCode.MY_BRAND]: this.signalData.brandKey,
      [PosthogEventCode.MY_COMPETITORS]: competitorKeys.join(';'),
      [PosthogEventCode.ORIGIN]: startChatOptions.origin,
      [PosthogEventCode.PROMPT_CONTENT]: SIGNAL_ANALYSIS_PROMPT
    });
    void this.startChatAndNavigate(SIGNAL_ANALYSIS_PROMPT, startChatOptions);
  }

  private async startChatAndNavigate(message: string, options: PolarisStartChatOptionsVm): Promise<void> {
    try {
      const chatId = await this.polarisRuntimeFacadeService.startChat(message, options);
      await this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.CHAT}/${chatId}`]);
    } catch {
      // Stay on signals when chat creation fails.
    }
  }

}
